import { useEffect, useRef, useState } from 'react';

import awsConfig from '../aws-exports';
import { getJwtToken } from '../components/common/Auth/JwtHelper';

const PROXY_URL_PATH = 'licenseAcquisition';
const ARCUS_DRM_PROVIDER = 'arcus';

/**
 * Custom hook to fetch and manage the Arcus License Agreement (LA) URL.
 *
 * @param {ArcusLaUrlInput | undefined} input - The input parameters required to fetch the LA URL.
 * @returns {ArcusLaUrl} An object containing the fetched LA URL and the loading state.
 *
 * @typedef {Object} ArcusLaUrlInput
 * @property {string} configAlias - The configuration alias.
 * @property {string} contentRef - The content reference.
 * @property {string} propositionContentId - The proposition content ID.
 * @property {string} protection - The protection parameter.
 *
 * @typedef {Object} ArcusLaUrl
 * @property {string} laUrl - The fetched LA URL.
 * @property {boolean} loading - The loading state indicating whether the URL is being fetched.
 *
 * @example
 * const { laUrl, loading } = useArcusLaUrl({
 *   configAlias: 'exampleAlias',
 *   contentRef: 'exampleContentRef',
 *   propositionContentId: 'examplePropositionId',
 *   protection: 'exampleProtection'
 * });
 */
const useLicenseAcquisitionUrl = (input: ArcusLaUrlInput | undefined): ArcusLaUrl => {
	const isMounted = useRef(true);

	const [laUrl, setLaUrl] = useState('');
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		// Cleanup function to prevent state updates on unmounted component
		return (): void => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (isMounted.current && input) {
			const fetchData = async (): Promise<void> => {
				// Compose the proxy URL with the input parameters
				let proxyUrl: URL;
				if (input.drmProvider.toLocaleLowerCase() === ARCUS_DRM_PROVIDER) {
					proxyUrl = new URL(`${awsConfig.arcusLicenseAcquisitionEndpointUrl}/${PROXY_URL_PATH}`);
				} else{
					proxyUrl = new URL(`${awsConfig.licenseAcquisitionEndpointUrl}/${PROXY_URL_PATH}`);
				}

				for (const [key, value] of Object.entries(input)) {
					proxyUrl.searchParams.append(key, value);
				}

				const jwtToken = await getJwtToken();

				fetch(proxyUrl.toString(), {
					method: 'GET',
					mode: 'cors',
					headers: {
						Authorization: `Bearer ${jwtToken}`,
						'Content-Type': 'text/plain'
					}
				})
					.then(async (response) => {
						if (response.ok) return response.text();
						throw new Error(await response.text());
					})
					.then((laUrl) => {
						if (isMounted.current) setLaUrl(laUrl);
					})
					.catch((err) => {
						// eslint-disable-next-line no-console
						console.error('Error retrieving license URL:', err);
					});

				setLoading(false);
			};

			fetchData();
		}
	}, [input]);

	return {
		laUrl,
		loading
	};
};

export type ArcusLaUrlInput = {
	configAlias: string;
	contentRef: string;
	propositionContentId: string;
	protection: string;
	drmProvider: string;
};

export type ArcusLaUrl = {
	laUrl: string;
	loading: boolean;
};

export default useLicenseAcquisitionUrl;
